import { useEffect, useState, type FC } from "react";
import { Form, Input, Select, ConfigProvider } from "antd";
import useFormHandling from "../hooks/useFormHandling";
import { FieldCard } from "../../../shared/layout/FieldCard";
import { type FieldDetailsType } from "../types";
import { DropdownEmptyState } from "../../../shared/layout/DropdownEmptyState";
import useAxios from "../../../shared/custom-hooks/useAxios";
import { type ProtocolInterface } from "./type";
import { useAppState } from "../../../lib/appContext/AppContext";
import { useNewTrialState } from "../hooks/NewTrialStateProvider";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../lib/auth/AuthContext";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import PlatFormAPIAxiosInstance from "../../../shared/utils/platFormAxios";
import { ReactComponent as InfoIcon } from "../../../assets/images/info.svg";
import "./trialInfo.less";
import ProtocolDrawer from "../../../shared/layout/ProtocolDrawer";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";

interface TrailInfoComponentProps {
  trialData?: any;
  form: any;
}

const TrailInfoComponent: FC<TrailInfoComponentProps> = ({
  trialData = {},
  form,
}) => {
  const { t } = useTranslation();
  const { RangePicker, handleFieldSelection, fieldData } = useFormHandling();
  const { user } = useAuth();
  const { stateValue, setStateValue } = useAppState();
  const { orgId, workspaceId, farmId } = useTrialsRouter();
  const headers = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const [orgCountry, setOrgCountry] = useState<string>("");

  /**
   * Fetch the organization country based on the orgId and update the state.
   */
  useEffect(() => {
    if (orgId) {
      PlatFormAPIAxiosInstance.get(`/orgs/${orgId}?attributes=workspaces`)
        .then((res: any) => {
          const fetchedCountry = res?.data?.country ?? "";
          setOrgCountry(String(fetchedCountry));
        })
        .catch((err: any) => {
          console.error("Error fetching org country:", err);
        });
    }
  }, [orgId]);

  const protocolsUrl = orgCountry
    ? `/protocols/dropdown?country=${orgCountry}`
    : null;

  const { response: protocolsList, loading: protocolsLoading } = useAxios(
    protocolsUrl ?? "",
    false,
    headers
  );

  const { trialInfo, setTrialInfo } = useNewTrialState();
  const [assignee, setAssignee] = useState<any>([]);
  const [assigneeList, setAssigneeList] = useState<any>([]);
  const [loader, setLoader] = useState(false);
  const propertyId = farmId ?? fieldData?.[0]?.property_id;
  const [open, setOpen] = useState(false);

  const getWorkers = async (): Promise<any> => {
    setLoader(true);
    const response = orgId
      ? await PlatFormAPIAxiosInstance.post(
          `/orgs/${orgId}/properties/ids/workers`,
          {
            ids: [propertyId ?? ""],
          }
        )
      : { data: { content: [] } };
    return response.data;
  };

  useEffect(() => {
    if (propertyId) {
      getWorkers()
        .then((data) => {
          data?.content?.sort((a: any, b: any) => {
            const nameA = a?.name ? a?.name?.toLowerCase?.() : "";
            const nameB = b?.name ? b?.name?.toLowerCase?.() : "";
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
          });
          setAssignee(data?.content ?? []);
          setLoader(false);
        })
        .catch((e) => {
          console.error("error fetching data: assignee data", e);
          setLoader(false);
        });
    }
  }, [orgId, propertyId]);

  useEffect(() => {
    setAssigneeList(
      Array.isArray(assignee) && assignee?.length
        ? assignee?.map((data: any) => {
            return { value: data?.id, label: data?.name };
          })
        : []
    );
  }, [assignee]);

  useEffect(() => {
    if (Array.isArray(assigneeList) && assigneeList?.length) {
      setStateValue({
        ...stateValue,
        assigneeValue: assigneeList,
      });
      form.setFieldValue("assignee_id", user.id);
      if (trialData?.id && trialData?.assignee_id) {
        form.setFieldValue("assignee_id", trialData?.assignee_id);
      }
    }
  }, [assigneeList, trialData?.id]);

  // Sort protocolsList by name
  const sortedProtocolsList = Array.isArray(protocolsList)
    ? protocolsList.sort((a, b) => a?.name.localeCompare(b.name))
    : [];

  useEffect(() => {
    if (trialData?.id && sortedProtocolsList?.length > 0) {
      checkArchiveProtocolExist();
    }
  }, [sortedProtocolsList, trialData]);

  function checkArchiveProtocolExist(): void {
    if (
      trialData?.id &&
      !sortedProtocolsList.some(
        (protocol: ProtocolInterface) => protocol?.id === trialData?.protocol_id
      )
    ) {
      sortedProtocolsList.push({
        id: trialData?.protocol_id,
        name: trialData?.protocol_name,
      });
    }
  }

  useEffect(() => {
    setTrialInfo({
      ...trialInfo,
      field_ids: trialInfo?.field_ids,
    });
    // form.setFieldValue("field_ids", trialInfo?.field_ids[0]);
  }, [trialInfo?.field_ids]);

  const openDrawer = (): void => {
    setOpen(true);
  };

  const onClose = (): void => {
    setOpen(false);
  };

  const protocolId = form.getFieldValue("protocol_id");
  const selectedProtocol = form.getFieldValue("protocol_id");
  return (
    <>
      <div className="rates-main">
        <h3 className="title">
          {formatTranslation(t("newTrial.trialInfo.title"))}
        </h3>
      </div>
      <Form.Item
        key="name"
        name="name"
        label={formatTranslation(t("trials.tableTitle1"))}
        rules={[{ required: true }]}
      >
        <Input
          data-testid="trail-name"
          className="trialInfo-input"
          onChange={(e) => {
            form.setFieldsValue({ trialName: e.target.value });
          }}
          placeholder={formatTranslation(
            t("configure.newtrialname.input.placeholder")
          )}
        />
      </Form.Item>

      <ConfigProvider
        renderEmpty={() => <DropdownEmptyState isloading={protocolsLoading} />}
      >
        <Form.Item
          key={"protocol_id"}
          name={"protocol_id"}
          rules={[{ required: true }]}
          label={formatTranslation(t("newTrial.trialInfo.protocolSelection"))}
        >
          <Select
            loading={protocolsLoading}
            showSearch
            data-testid="protocol-selection"
            placeholder={formatTranslation(
              t("newTrial.trialInfo.trialPlaceholder")
            )}
            onChange={(value) => {
              const selectedProtocol = sortedProtocolsList.find(
                (protocol: ProtocolInterface) => protocol?.id === value
              );
              if (selectedProtocol) {
                form.setFieldsValue({
                  protocol_id: selectedProtocol?.id, // Fix: Set the correct field
                });
              }
            }}
            value={selectedProtocol}
            filterOption={(input, option) => {
              const label = option?.label?.toString() ?? "";
              const inputLower = input.toString().toLowerCase();
              const labelLower = label.toLowerCase();
              const labelUpper = label.toUpperCase();
              return (
                labelLower.includes(inputLower) ||
                labelUpper.includes(inputLower)
              );
            }}
            options={sortedProtocolsList?.map(
              (protocol: ProtocolInterface) => ({
                value: protocol?.id,
                label: protocol?.name,
                "data-testid": protocol?.id,
              })
            )}
          ></Select>
          {selectedProtocol && (
            <div className="info-icon" onClick={openDrawer}>
              <InfoIcon width={"16px"} height={"16px"} />
              <p>Protocol info</p>
            </div>
          )}
        </Form.Item>
      </ConfigProvider>

      <div className="grid-row">
        <Form.Item
          label={formatTranslation(t("newTrial.trialInfo.trialDate"))}
          key="rangePicker"
          name="rangePicker"
          rules={[{ required: true }]}
        >
          <RangePicker
            style={{ width: "100%" }}
            data-testid="range-picker"
            onChange={(date: any) => {
              form.setFieldsValue({ rangePicker: date });
            }}
          />
        </Form.Item>

        <ConfigProvider
          renderEmpty={() => <DropdownEmptyState isloading={loader} />}
        >
          <Form.Item
            key={"assignee_id"}
            name={"assignee_id"}
            label={formatTranslation(t("newTrial.trialInfo.assignee"))}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={loader}
              data-testid="assignee-input"
              placeholder="Select team member responsible"
              allowClear
              onChange={(value) => {
                form.setFieldsValue({ assignee_id: value });
              }}
              filterOption={(input, option) => {
                const label = option?.label?.toString() ?? "";
                const inputLower = input.toString().toLowerCase();
                const labelLower = label.toLowerCase();
                const labelUpper = label.toUpperCase();
                return (
                  labelLower.includes(inputLower) ||
                  labelUpper.includes(inputLower)
                );
              }}
              options={assigneeList}
            ></Select>
          </Form.Item>
        </ConfigProvider>
      </div>

      <Form.Item
        label={formatTranslation(t("newTrial.trialInfo.fields"))}
        key="field_ids"
        name="field_ids"
        rules={[{ required: true }]}
      >
        <div className="field-map">
          <div className="card-main">
            {farmId ? (
              fieldData
                ?.sort((a: any, b: any) => a?.name.localeCompare(b?.name))
                .map((field: FieldDetailsType, index: number) => (
                  <FieldCard
                    key={`${field.name}-${index}`}
                    fieldName={field?.name}
                    fieldArea={field?.declared_area}
                    image={field?.image}
                    id={field?.id}
                    onChange={(e: any) => {
                      handleFieldSelection(e.target.value);
                    }}
                    geojson={field?.geometry}
                  />
                ))
            ) : (
              <p style={{ marginLeft: 10 }}>
                {formatTranslation(t("newTrial.trialInfo.noInfo"))}
              </p>
            )}
          </div>
        </div>
      </Form.Item>
      {protocolId && (
        <ProtocolDrawer
          protocolId={protocolId}
          open={open}
          onClose={onClose}
          extraHeaders={headers}
        />
      )}
    </>
  );
};

export default TrailInfoComponent;
